import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Copy, Database, Hash, Inbox, Loader2, MapPin, Pencil, ShieldCheck, Sparkles, Upload, Users } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type HouseNumberInAddition, type AddressCase, type AddressCheckResult, type AddressCheckSettings, type AddressTenant, type ShippingAddress } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { msg, t, tn } from "@/lib/i18n";

const errMsg = (e: unknown, f: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);

const MODES: { value: AddressCheckSettings["mode"]; label: string; hint: string }[] = [
  { value: "logiful", label: "Logiful prüft", hint: "Klärungsfälle landen hier in der Warteschlange – der Kunde sieht „In Prüfung bei Logiful“" },
  { value: "transfer", label: "Trotzdem übertragen", hint: "Eindeutige Korrekturen gelten, unklare Adressen gehen unverändert ans Lager" },
  { value: "customer", label: "Kunde korrigiert", hint: "Auftrag wird zurückgehalten, der Kunde sieht den Grund und korrigiert selbst" },
];

/** Logiful-intern: DHL-Leitcodierung – Klärungsfälle, Einstellung je Kunde, Leitdaten */
export function AdminAddressPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "queue";
  const dhl = useQuery({ queryKey: ["dhl"], queryFn: api.address.dhl, refetchInterval: (q) => (q.state.data?.latest?.status === "importing" ? 2500 : false) });
  const queue = useQuery({ queryKey: ["address-queue"], queryFn: () => api.address.queue("logiful"), refetchInterval: 60_000 });
  const hnr = useQuery({ queryKey: ["address-hnr-addition"], queryFn: api.address.houseNumberInAddition, refetchInterval: 5 * 60_000 });
  const TABS = [
    { key: "queue", label: "Klärungsfälle", icon: Inbox, count: queue.data?.length },
    { key: "hnr", label: t("Hausnummer im Zusatz"), icon: Hash, count: hnr.data?.length },
    { key: "tenants", label: "Kunden", icon: Users },
    { key: "data", label: "DHL-Leitdaten", icon: Database },
  ];
  const noData = dhl.data && !dhl.data.active;

  return (
    <div className="pb-16">
      <PageHeader title="Adressprüfung" description="DHL-Leitcodierung vor der Übergabe ans Lager: eindeutige Fehler werden automatisch korrigiert, unklare Adressen klärt Logiful hier – bevor Strafgebühren entstehen." />
      <div className="px-4 md:px-8 space-y-4 max-w-6xl">
        {noData && (
          <Card className="p-4 flex items-start gap-3 border-warning/30 bg-warning-soft/40">
            <AlertTriangle className="size-4 text-warning mt-0.5 shrink-0" />
            <div className="flex-1 text-[13px]">
              <p className="font-medium">Noch keine DHL-Leitdaten – die Adressprüfung ist inaktiv</p>
              <p className="text-fg-muted mt-0.5">Lade die DataFactory-Lieferung (ZIP) hoch. Danach werden alle neuen Aufträge geprüft.</p>
            </div>
            <Button size="sm" onClick={() => setParams({ tab: "data" }, { replace: true })}>Leitdaten hochladen</Button>
          </Card>
        )}
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setParams({ tab: t.key }, { replace: true })} className={cx("h-8 px-3 rounded-md text-[13px] font-medium inline-flex items-center gap-1.5", tab === t.key ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
              <t.icon className="size-3.5" /> {t.label}
              {!!t.count && <span className="ml-0.5 min-w-5 h-5 px-1.5 rounded-full bg-warning text-white text-[11px] grid place-items-center tabular-nums">{t.count}</span>}
            </button>
          ))}
        </div>
        {tab === "queue" && <QueueTab cases={queue.data} loading={queue.isLoading} />}
        {tab === "hnr" && <HouseNumberTab rows={hnr.data} loading={hnr.isLoading} />}
        {tab === "tenants" && <TenantsTab />}
        {tab === "data" && <DataTab />}
      </div>
    </div>
  );
}

// ── Hausnummer nur im Adresszusatz (schon beim Lager) ────────────────

const STATUS_TEXT: Record<string, string> = { submitted: msg("beim Lager"), in_progress: msg("in Bearbeitung") };

function HouseNumberTab({ rows, loading }: { rows: HouseNumberInAddition[] | undefined; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!rows?.length)
    return (
      <EmptyState icon={<CheckCircle2 className="size-5 text-success" />} title={t("Keine betroffenen Aufträge beim Lager")}>
        {t("Neue Aufträge werden vor der Übergabe automatisch bereinigt.")}
      </EmptyState>
    );
  const copy = (r: HouseNumberInAddition) => void navigator.clipboard?.writeText([r.suggestion.street, r.suggestion.addition].filter(Boolean).join("\n")).then(() => toast.success(t("Korrigierte Adresse kopiert")));
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-border text-[13px]">
        <p className="font-medium">{tn(rows.length, "{n} Auftrag beim Lager mit Hausnummer nur im Adresszusatz", "{n} Aufträge beim Lager mit Hausnummer nur im Adresszusatz")}</p>
        <p className="text-fg-muted mt-0.5">{t("Vor der automatischen Bereinigung übergeben. Bitte im Lager korrigieren, solange noch nicht versendet – Connect ändert diese Aufträge nicht selbst.")}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-[11.5px] text-fg-subtle text-left">
            <tr className="border-b border-border">
              <th className="px-4 py-2 font-medium">{t("Auftrag")}</th>
              <th className="px-3 py-2 font-medium">{t("Kunde")}</th>
              <th className="px-3 py-2 font-medium">{t("Jetzt beim Lager")}</th>
              <th className="px-3 py-2 font-medium">{t("Richtig")}</th>
              <th className="px-3 py-2 font-medium">{t("Status")}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.orderId}>
                <td className="px-4 py-2.5 font-medium tabular-nums whitespace-nowrap">{r.orderNumber}<span className="block text-[11.5px] font-normal text-fg-subtle">{fmtRelative(r.placedAt)}</span></td>
                <td className="px-3 py-2.5">{r.tenant}{r.customerNumber && <span className="block text-[11.5px] text-fg-subtle tabular-nums">{t("Kd-Nr.")} {r.customerNumber}</span>}</td>
                <td className="px-3 py-2.5 text-fg-muted">{r.street}<span className="block">{t("Zusatz")}: „{r.addition}“</span></td>
                <td className="px-3 py-2.5">{r.suggestion.street}{r.suggestion.addition && <span className="block text-fg-muted">{t("Zusatz")}: „{r.suggestion.addition}“</span>}</td>
                <td className="px-3 py-2.5 whitespace-nowrap"><Badge tone="info">{t(STATUS_TEXT[r.status] ?? r.status)}</Badge></td>
                <td className="px-3 py-2.5 text-right"><button onClick={() => copy(r)} className="size-8 inline-grid place-items-center rounded-lg text-fg-subtle hover:text-fg hover:bg-surface-2" aria-label={t("Korrigierte Adresse kopieren")}><Copy className="size-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Klärungsfälle ────────────────────────────────────────────────────

const fmtAddr = (a: ShippingAddress) => [[a.company, [a.firstName, a.lastName].filter(Boolean).join(" ")].filter(Boolean).join(" · "), [a.street, a.houseNumber].filter(Boolean).join(" "), a.addition, `${a.zip} ${a.city}`].filter(Boolean);

function QueueTab({ cases, loading }: { cases: AddressCase[] | undefined; loading: boolean }) {
  if (loading) return <Skeleton className="h-60 w-full" />;
  if (!cases?.length)
    return (
      <EmptyState icon={<CheckCircle2 className="size-5 text-success" />} title="Keine offenen Klärungsfälle">
        Alle Adressen sind geprüft. Neue Fälle erscheinen hier automatisch.
      </EmptyState>
    );
  return (
    <div className="space-y-3">
      {cases.map((c) => <CaseCard key={c.id} c={c} />)}
    </div>
  );
}

/** Hausnummer aus der Straßenzeile, damit ein Straßenvorschlag sie behält */
const trailingNumber = (street: string) => /\s(\d{1,4}\s?[a-zA-Z]?(?:\s?[-/]\s?\d{1,4}\s?[a-zA-Z]?)?)\s*$/.exec(street)?.[1] ?? "";

function CaseCard({ c }: { c: AddressCase }) {
  const [editing, setEditing] = useState<ShippingAddress | null>(null);
  const apply = (s: AddressCase["check"]["suggestions"][number]) => {
    const hn = c.shippingAddress.houseNumber ? "" : trailingNumber(c.shippingAddress.street);
    setEditing({ ...c.shippingAddress, street: hn ? `${s.street} ${hn}` : s.street, zip: s.zip, city: s.city });
  };
  return (
    <Card className="overflow-hidden">
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-fg-muted min-w-0 flex-1">
            <span className="font-medium text-fg whitespace-nowrap">{c.tenantName}</span>
            <span>·</span>
            <span className="tabular-nums whitespace-nowrap">{c.orderNumber}</span>
            <span>·</span>
            <span className="whitespace-nowrap">{fmtRelative(c.placedAt)}</span>
            {c.check.kind !== "street" && <Badge>{c.check.kind === "packstation" ? "Packstation" : "Postfiliale"}</Badge>}
          </div>
          {!editing && <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => setEditing({ ...c.shippingAddress })}>Korrigieren</Button>}
        </div>
        <div className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-6 gap-y-3">
          <div className="flex gap-2 min-w-0">
            <MapPin className="size-4 text-fg-subtle mt-0.5 shrink-0" />
            <address className="not-italic text-[13.5px] leading-relaxed min-w-0">{fmtAddr(c.shippingAddress).map((l) => <div key={l} className="break-words">{l}</div>)}</address>
          </div>
          <div className="space-y-2 min-w-0">
            <ul className="space-y-1">
              {c.check.issues.map((i) => (
                <li key={i.code} className="flex items-start gap-1.5 text-[12.5px] text-warning"><AlertTriangle className="size-3.5 mt-0.5 shrink-0" /> <span>{i.message}</span></li>
              ))}
            </ul>
            {c.check.suggestions.length > 0 && (
              <div>
                <div className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-subtle mb-1">Vorschläge aus den Leitdaten</div>
                <div className="flex flex-col items-start gap-1.5">
                  {c.check.suggestions.map((s) => (
                    <button key={`${s.street}${s.zip}`} onClick={() => apply(s)} className="max-w-full min-h-7 px-2.5 py-1 rounded-md border border-border bg-surface-2/50 hover:bg-surface-2 text-[12.5px] inline-flex items-center gap-1.5 text-left">
                      <Sparkles className="size-3 text-coral shrink-0" />
                      <span className="min-w-0">{s.street} · {s.zip} {s.city}</span>
                      <span className="text-fg-subtle tabular-nums whitespace-nowrap">{Math.round(s.score * 100)} %</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {editing && <FixForm c={c} initial={editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function FixForm({ c, initial, onClose }: { c: AddressCase; initial: ShippingAddress; onClose: () => void }) {
  const qc = useQueryClient();
  const [a, setA] = useState<ShippingAddress>(initial);
  useEffect(() => setA(initial), [initial]);
  const [result, setResult] = useState<AddressCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const set = (k: keyof ShippingAddress, v: string) => setA((x) => ({ ...x, [k]: v }));

  // Live-Prüfung gegen die Leitdaten (verzögert beim Tippen)
  useEffect(() => {
    const t = setTimeout(async () => {
      setChecking(true);
      try { setResult(await api.address.check(a)); } catch { setResult(null); } finally { setChecking(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [a]);

  const save = useMutation({
    mutationFn: ({ address, confirm }: { address: ShippingAddress; confirm: boolean }) => api.address.fix(c.id, address, confirm),
    onSuccess: (_d, v) => {
      toast.success(`${c.orderNumber}: Adresse ${v.confirm ? "bestätigt" : "korrigiert"} – Auftrag wird neu geprüft`);
      void qc.invalidateQueries({ queryKey: ["address-queue"] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, "Speichern fehlgeschlagen")),
  });
  // Ist die Prüfung „korrigiert“, die korrigierte Fassung speichern
  const final = result?.status === "corrected" && result.address ? (result.address as ShippingAddress) : a;
  const field = (k: keyof ShippingAddress, label: string, cls = "") => (
    <label className={cx("block", cls)}>
      <span className="text-[11.5px] font-medium text-fg-muted">{label}</span>
      <Input value={(a[k] as string | undefined) ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
    </label>
  );

  return (
    <div className="border-t border-border bg-surface-2/30 p-4 space-y-3">
      <div className="grid grid-cols-6 gap-2">
        {field("company", "Firma", "col-span-6 sm:col-span-3")}
        {field("addition", "Adresszusatz / Postnummer", "col-span-6 sm:col-span-3")}
        {field("street", "Straße", "col-span-4")}
        {field("houseNumber", "Hausnr.", "col-span-2")}
        {field("zip", "PLZ", "col-span-2")}
        {field("city", "Ort", "col-span-4")}
      </div>
      <CheckResult result={result} checking={checking} />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button
          title="Adresse ist korrekt, auch wenn die Leitdaten sie (noch) nicht kennen – z. B. Neubaugebiet"
          onClick={() => save.mutate({ address: a, confirm: true })}
          loading={save.isPending && save.variables?.confirm}
          icon={<ShieldCheck className="size-4" />}
        >
          Als geprüft bestätigen
        </Button>
        <Button variant="primary" disabled={result?.status === "manual"} onClick={() => save.mutate({ address: final, confirm: false })} loading={save.isPending && !save.variables?.confirm}>
          Speichern & freigeben
        </Button>
      </div>
    </div>
  );
}

function CheckResult({ result, checking }: { result: AddressCheckResult | null; checking: boolean }) {
  if (checking && !result) return <p className="text-[12.5px] text-fg-muted inline-flex items-center gap-1.5"><Loader2 className="size-3.5 animate-spin" /> Prüfe gegen DHL-Leitdaten …</p>;
  if (!result) return null;
  const tone = result.status === "manual" ? "border-warning/30 bg-warning-soft/40" : "border-success/25 bg-success-soft/50";
  return (
    <div className={cx("rounded-lg border px-3 py-2 text-[12.5px]", tone)}>
      {result.status === "manual" ? (
        <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="size-3.5 text-warning" /> {result.issues.map((i) => i.message).join(" · ")}</p>
      ) : (
        <p className="font-medium flex items-center gap-1.5">
          <CheckCircle2 className="size-3.5 text-success" /> {result.status === "ok" ? "Adresse gültig" : "Gültig nach Korrektur"}
          {result.leitcode && <span className="ml-1 font-mono text-fg-muted">Leitcode {result.leitcode}</span>}
          {checking && <Loader2 className="size-3 animate-spin text-fg-subtle" />}
        </p>
      )}
      {result.changes.length > 0 && <p className="mt-1 text-fg-muted">Wird gespeichert als: {result.changes.map((c) => `${c.field === "street" ? "Straße" : c.field === "zip" ? "PLZ" : c.field === "city" ? "Ort" : c.field} „${c.to}“`).join(", ")}</p>}
    </div>
  );
}

// ── Kunden ───────────────────────────────────────────────────────────

function TenantsTab() {
  const q = useQuery({ queryKey: ["address-tenants"], queryFn: api.address.tenants });
  if (q.isLoading) return <Skeleton className="h-60 w-full" />;
  return (
    <Card className="divide-y divide-border">
      <div className="px-4 py-3 text-[12.5px] text-fg-muted">Standard für neue Kunden: <b className="text-fg">Logiful prüft</b>, eindeutige Fehler automatisch korrigieren. Korrigierte Aufträge gehen mit dem FFN-Attribut „optimization = level-address“ bzw. „level-packstation“ ans Lager.</div>
      {q.data?.map((t) => <TenantRow key={t.id} t={t} />)}
    </Card>
  );
}

function TenantRow({ t }: { t: AddressTenant }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (s: AddressCheckSettings) => api.address.setTenant(t.id, s),
    onSuccess: () => { toast.success(`${t.name}: gespeichert`); void qc.invalidateQueries({ queryKey: ["address-tenants"] }); },
    onError: (e) => toast.error(errMsg(e, "Speichern fehlgeschlagen")),
  });
  const s = save.variables ?? t.settings;
  const set = (patch: Partial<AddressCheckSettings>) => save.mutate({ ...s, ...patch });
  return (
    <div className={cx("px-4 py-3 flex flex-wrap items-center gap-3", !s.enabled && "opacity-70")}>
      <div className="min-w-[180px] flex-1">
        <div className="font-medium text-[13.5px]">{t.name}</div>
        {t.openCases > 0 && <div className="text-[12px] text-warning">{t.openCases} offene Klärungsfälle</div>}
      </div>
      <Toggle on={s.enabled} onChange={(v) => set({ enabled: v })} label="Prüfung" />
      <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" aria-disabled={!s.enabled}>
        {MODES.map((m) => (
          <button key={m.value} disabled={!s.enabled} title={m.hint} onClick={() => set({ mode: m.value })} className={cx("h-7 px-2.5 rounded-md text-[12.5px]", s.mode === m.value ? "bg-surface shadow-sm font-medium" : "text-fg-muted hover:text-fg")}>
            {m.label}
          </button>
        ))}
      </div>
      <Toggle on={s.autoCorrect} onChange={(v) => set({ autoCorrect: v })} label="Autokorrektur" disabled={!s.enabled} />
    </div>
  );
}

function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} className="inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted disabled:opacity-50">
      <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors", on ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3 rounded-full bg-surface transition-transform", on && "translate-x-3")} /></span>
      {label}
    </button>
  );
}

// ── Leitdaten ────────────────────────────────────────────────────────

const STEP_LABEL = (step: string) => (step === "meta" ? "Orte, Packstationen, Filialen" : step === "activate" ? "Umschalten" : step === "upload" ? "Hochgeladen" : `Straßen PLZ ${step}xxxx`);

function DataTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["dhl"], queryFn: api.address.dhl, refetchInterval: (x) => (x.state.data?.latest?.status === "importing" ? 2500 : false) });
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (f: File) => api.address.uploadDhl(f),
    onSuccess: () => { toast.success("Hochgeladen – Import läuft (ca. 3–5 Minuten)"); void qc.invalidateQueries({ queryKey: ["dhl"] }); },
    onError: (e) => toast.error(errMsg(e, "Upload fehlgeschlagen")),
  });
  if (q.isLoading) return <Skeleton className="h-60 w-full" />;
  const { active, latest } = q.data!;
  const importing = latest?.status === "importing" ? latest : null;
  const failed = latest?.status === "failed" ? latest : null;

  return (
    <div className="space-y-3">
      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[240px]">
            <h2 className="font-semibold flex items-center gap-2"><Database className="size-4 text-fg-subtle" /> Aktive Leitdaten</h2>
            {active ? (
              <>
                <p className="mt-1 text-[13px] text-fg-muted">{active.version} · aktiv seit {fmtRelative(active.activatedAt!)}</p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Straßenabschnitte" value={active.stats.streets} />
                  <Stat label="Orte & Ortsteile" value={active.stats.localities} />
                  <Stat label="Packstationen" value={active.stats.packstations} />
                  <Stat label="Postfilialen" value={active.stats.filialen} />
                </div>
              </>
            ) : (
              <p className="mt-1 text-[13px] text-fg-muted">Noch keine – die Adressprüfung ist inaktiv.</p>
            )}
          </div>
          <div>
            <input ref={input} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
            <Button variant="primary" icon={<Upload className="size-4" />} loading={upload.isPending} disabled={!!importing} onClick={() => input.current?.click()}>
              {active ? "Neue Lieferung hochladen" : "Leitdaten hochladen"}
            </Button>
            <p className="mt-1.5 text-[11.5px] text-fg-subtle max-w-[240px]">DataFactory-ZIP der Deutschen Post Direkt (vierteljährlich). Die bisherigen Daten bleiben bis zur Umschaltung aktiv.</p>
          </div>
        </div>
      </Card>
      {importing && (
        <Card className="p-4">
          <p className="font-medium text-[13px] flex items-center gap-2"><Loader2 className="size-4 animate-spin text-fg-muted" /> Import läuft: {STEP_LABEL(importing.progress?.step ?? "upload")}</p>
          <div className="mt-2 h-1.5 rounded-full bg-surface-2 overflow-hidden"><div className="h-full bg-ink transition-all" style={{ width: `${Math.round(((importing.progress?.done ?? 0) / (importing.progress?.total ?? 12)) * 100)}%` }} /></div>
        </Card>
      )}
      {failed && (
        <Card className="p-4 border-danger/25 bg-danger-soft">
          <p className="font-medium text-[13px] text-danger">Import fehlgeschlagen: {failed.error}</p>
          <p className="text-[12.5px] text-fg-muted mt-0.5">Die bisherigen Leitdaten bleiben aktiv.</p>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | undefined }): ReactNode {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="text-[17px] font-semibold tabular-nums">{value != null ? fmtNumber(value) : "–"}</div>
      <div className="text-[11.5px] text-fg-muted">{label}</div>
    </div>
  );
}
